'use client';

import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { ScreenName, screens } from '../utils';

type ActiveScreens = {
  [name in ScreenName]: boolean;
};

type ActiveScreensQueryLists = {
  [name in ScreenName]: MediaQueryList;
};

const listsToState = (lists: ActiveScreensQueryLists): ActiveScreens => ({
  xs: lists.xs.matches,
  sm: lists.sm.matches,
  md: lists.md.matches,
  lg: lists.lg.matches,
  xl: lists.xl.matches,
  xxl: lists.xxl.matches,
});

const createChangeHandler = (
  name: ScreenName,
  setScreenState: Dispatch<SetStateAction<ActiveScreens>>,
) => {
  return ({ matches }: MediaQueryListEvent) => {
    setScreenState(prev => ({
      ...prev,
      [name]: matches,
    }));
  };
};

/**
 * Returns all active screen names.
 * The returned object's keys are the {@link ScreenName}s and the values indicates
 * if the screen name is currently active (matches the underlying media query).
 *
 * ```tsx
 * const activeScreens = useActiveScreenNames();
 *
 * if (activeScreens.md) {
 *   ...
 * }
 * ```
 *
 * This is a hook which keeps track of all the available screen names.
 * Normally you'd be better off using {@link useScreenName} or {@link useScreenNameAtLeast}.
 */
export const useActiveScreenNames = (
  ssrValues: ActiveScreens = {
    xs: false,
    sm: false,
    md: false,
    lg: false,
    xl: false,
    xxl: false,
  },
): ActiveScreens => {
  const [screenState, setScreenState] = useState<ActiveScreens>(ssrValues);

  useEffect(() => {
    const lists = {
      xs: window.matchMedia(`(min-width: ${screens.xs}px)`),
      sm: window.matchMedia(`(min-width: ${screens.sm}px)`),
      md: window.matchMedia(`(min-width: ${screens.md}px)`),
      lg: window.matchMedia(`(min-width: ${screens.lg}px)`),
      xl: window.matchMedia(`(min-width: ${screens.xl}px)`),
      xxl: window.matchMedia(`(min-width: ${screens.xxl}px)`),
    };

    setScreenState(listsToState(lists));

    const handleXsChange = createChangeHandler('xs', setScreenState);
    const handleSmChange = createChangeHandler('sm', setScreenState);
    const handleMdChange = createChangeHandler('md', setScreenState);
    const handleLgChange = createChangeHandler('lg', setScreenState);
    const handleXlChange = createChangeHandler('xl', setScreenState);
    const handleXxlChange = createChangeHandler('xxl', setScreenState);

    lists.xs.addEventListener('change', handleXsChange);
    lists.sm.addEventListener('change', handleSmChange);
    lists.md.addEventListener('change', handleMdChange);
    lists.lg.addEventListener('change', handleLgChange);
    lists.xl.addEventListener('change', handleXlChange);
    lists.xxl.addEventListener('change', handleXxlChange);

    return () => {
      lists.xs.removeEventListener('change', handleXsChange);
      lists.sm.removeEventListener('change', handleSmChange);
      lists.md.removeEventListener('change', handleMdChange);
      lists.lg.removeEventListener('change', handleLgChange);
      lists.xl.removeEventListener('change', handleXlChange);
      lists.xxl.removeEventListener('change', handleXxlChange);
    };
  }, []);

  return screenState;
};
